import Constants from "expo-constants";
import { Platform } from "react-native";

export type DeploymentEnvironment = "local" | "preview" | "production";

function resolveDeploymentEnvironment(): DeploymentEnvironment {
  if (Platform.OS === "web") {
    // `window` is undefined during the static-export prerender step
    // (Node.js, not a browser); "local" there is harmless since hydration
    // re-evaluates this in the real browser immediately after.
    if (typeof window === "undefined") return "local";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "local";
    // Cloudflare Workers: use.<domain> is production, dev-use.<domain> (and
    // any *.workers.dev URL) is preview.
    if (host.startsWith("dev-use.") || host.endsWith(".workers.dev"))
      return "preview";
    return "production";
  }
  // Native builds: `ENVIRONMENT` in .env (or the eas.json build profile) reaches
  // the app through app.config.ts. DEVELOPMENT is the local DDEV stack, TESTING
  // the Coolify preview API; anything else, unset included, is production.
  const requested = String(
    Constants.expoConfig?.extra?.environment ?? "",
  ).toUpperCase();
  return requested === "DEVELOPMENT"
    ? "local"
    : requested === "TESTING"
      ? "preview"
      : "production";
}

const deploymentEnvironment = resolveDeploymentEnvironment();

type EnvironmentConstants = {
  appUrl: string;
  siteUrl: string;
  apiUrl: string;
  sentryAppDsn?: string;
};

// Sentry DSN is a public ingest endpoint (safe to commit). `local` omits it so
// dev errors don't reach the shared project.
const sentryAppDsn =
  "https://6a8b134d014dfcd2da0b4b7573a82881@o4504087623761920.ingest.us.sentry.io/4512109384237056";

const constantsByEnvironment: Record<
  DeploymentEnvironment,
  EnvironmentConstants
> = {
  local: {
    siteUrl: "http://localhost:3000",
    appUrl: "http://localhost:8081",
    apiUrl: "https://durance-api.ddev.site",
  },
  preview: {
    siteUrl: "https://durance-website.pages.dev",
    appUrl: "https://dev-use.durance.app",
    apiUrl: "https://dev-api.durance.app",
    sentryAppDsn,
  },
  production: {
    siteUrl: "https://www.durance.app",
    appUrl: "https://use.durance.app",
    apiUrl: "https://api.durance.app",
    sentryAppDsn,
  },
};

const environmentConstants = constantsByEnvironment[deploymentEnvironment];

export const DEPLOYMENT_ENVIRONMENT = deploymentEnvironment;

// Cloudflare Turnstile site key (public). Off for the local stack so local
// sign-in and e2e don't need to reach Cloudflare; server secret lives only in
// the Silverstripe API environment.
export const TURNSTILE_SITE_KEY =
  deploymentEnvironment === "local" ? "" : "0x4AAAAAAFI308S_lRmWbNPE";
export const SITE_URL = environmentConstants.siteUrl;
export const APP_URL = environmentConstants.appUrl;
export const API_URL = process.env.EXPO_PUBLIC_API_URL?.trim() || environmentConstants.apiUrl;
export const SENTRY_APP_DSN = environmentConstants.sentryAppDsn;

// The hosted project's URL and publishable key are public, so they are baked in.
// Local development overrides them through the EXPO_PUBLIC_* variables.
export const SENTRY_ENABLED = Boolean(SENTRY_APP_DSN);
export const backendConfigured = Boolean(API_URL);

// RevenueCat SDK keys are public identifiers, never server secrets.
// `ios`/`android` are empty until durance is submitted to the App Store /
// Play Store — see go-live.md. `test` is the Test Store app's key (used by
// native dev builds). `web` is the Web Billing (Stripe-backed) app's key for
// this environment: production on use.durance.app, the sandbox key (Stripe
// test cards) everywhere else — local dev and dev-use.* / *.workers.dev previews.
const REVENUECAT_WEB_KEYS = {
  sandbox: "rcb_sb_VwngfWSwRvsmtquyKOOZtrACd",
  production: "rcb_nzHFJYyGKlkSQjOnhgRFZWJKRvVH",
} as const;

export const REVENUECAT_API_KEYS = {
  ios: "appl_zUwGfnCwhLlvaTJwqnoWspCIBGl",
  android: "goog_vjfjBeDLMPjwYycwLADLRarMApa",
  test: "test_ViIMRgWViYrRUnTthiqGZrUtYjF",
  web:
    deploymentEnvironment === "production"
      ? REVENUECAT_WEB_KEYS.production
      : REVENUECAT_WEB_KEYS.sandbox,
} as const;

export const STORAGE_KEY = "durance";

/** App Group / shared-storage identifiers the native widgets read from. */
export const WIDGET_SHARED_GROUP = {
  ios: "group.app.durance.use",
  androidPrefsName: "durance_widget_data",
} as const;
